import { Dispatch, SetStateAction, useState } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// local imports - types
import warningImage from 'assets/images/warning-image.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import DateUtil from 'utils/DateUtil'
import { IFilteredDoctorList } from 'types/HealthType'
import Loader from 'components/ui/Loader'
import NotificationDialog from 'components/ui/NotificationDialog'
import AppointmentModalHeader from 'components/home/on-campus-services/health-service/appointment-scheduler/AppointmentModalHeader'
import Scheduler from 'components/home/on-campus-services/health-service/appointment-scheduler/Scheduler'
import WeekSlider from 'components/home/on-campus-services/health-service/appointment-scheduler/WeekSlider'
import ScheduleSidebar from 'components/home/on-campus-services/health-service/appointment-scheduler/ScheduleSidebar'
import { useGetDoctorAppointmentTable } from 'components/home/on-campus-services/health-service/hooks'

/* Types */
interface IAppointmentModalProps {
  filterDate: Date
  doctors: IFilteredDoctorList[] | []
  selectedDoc: IFilteredDoctorList
  setSelectedDoc: Dispatch<SetStateAction<IFilteredDoctorList | undefined>>
  open: boolean
  closeModal: () => void
  openModal: () => void
  cancelAppointmentModal: () => void
  showNotification: () => void
  selectedDateStr?: string
}

interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

export default function CalendarDocAppointmentModal({
  filterDate: dateFromList,
  doctors,
  selectedDoc,
  setSelectedDoc,
  open,
  closeModal,
  openModal,
  showNotification,
  cancelAppointmentModal,
  selectedDateStr,
}: IAppointmentModalProps) {
  const { t } = useTranslation()

  /* Doctor List reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  const [diffDayNotification, setDiffDayNotification] = useState(false)
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)
  const isSelectedDifferentDate = moment(filterDate).isSame(dateFromList, 'day')

  /* GET Selected Doctor's Appointment Table List | Custom Hook  */
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedAppointment,
    setSelectedAppointment,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyAppointments,
  } = useGetDoctorAppointmentTable(selectedDoc.healthServiceDoctorId, dateFromList, true)

  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    showNotification()
    setSidebarVisible(false)
    // setSelectedAppointment(undefined)
    // setFilterDate(dateFromList)
    // setTableWeek(lastFilteredDate)
    closeModal()
  }

  /* Open Appointment Sidebar - Close Scheduler*/
  const openAppointmentSidebar = () => {
    if (isSelectedDifferentDate) {
      setSidebarVisible(true)
      closeModal()
    } else {
      closeModal()
      setDiffDayNotification(true)
    }
  }

  /* Close with cancel - Scheduler */
  const handleCloseOnCancel = () => {
    cancelAppointmentModal()
    setFilterDate(dateFromList)
    setSelectedAppointment(undefined)
    setTableWeek(lastFilteredDate)
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        closeOnEscape={false}
        closable={true}
        focusOnShow={false}
        header={<span>{t('appointment-modal.doctor-appointment')} (2/2)</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '95vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openAppointmentSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!selectedAppointment}
          />
        }>
        {/* Modal Header */}
        <AppointmentModalHeader
          selectedDateStr={selectedDateStr}
          filterDate={filterDate}
          availableDateRange={availableDates}
          setFilterDate={setFilterDate}
          setSelectedDoc={setSelectedDoc}
          doctor={selectedDoc}
          doctors={doctors}
          setTableWeek={setTableWeek}
        />

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <Scheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyAppointments={dailyAppointments}
            selectedAppointment={selectedAppointment}
            setSelectedAppointment={setSelectedAppointment}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedAppointment && (
        <ScheduleSidebar
          openModal={openModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          docInfo={selectedDoc}
          appointmentInfo={selectedAppointment}
        />
      )}

      <NotificationDialog
        display={diffDayNotification}
        imgSrc={warningImage}
        goHomeButton={true}
        extraButton={true}
        title={t('overlaypanel-texts.reservation-day-changed')}
        content={t('overlaypanel-texts.reservation-day-change-text')}
        firstButtonLabel={t('buttons.yes')}
        secondButtonLabel={t('buttons.no')}
        onHide={() => {
          setDiffDayNotification(false)
          openModal()
        }}
        onSubmit={() => {
          setDiffDayNotification(false)
          setSidebarVisible(true)
          closeModal()
        }}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()

  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.appointment')} onClick={onSave} />
    </ActionArea>
  )
}

const ActionArea = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: -4px;
  margin-top: 20px;
  border-bottom-right-radius: 15px !important;
  border-bottom-left-radius: 15px !important;
`
const CancelButton = styled(Button)`
  width: 255px !important;
  height: 50px;
  justify-content: center;
  background: #45bfe5;
  border-radius: 10px;

  :disabled {
    opacity: 0.4;
  }
`
const ConfirmButton = styled(Button)`
  width: 255px !important;
  height: 50px;
  justify-content: center;
  background: #45bfe5;
  border-radius: 10px;
`
